/**
 * bundledMaps — static map-data assets for cold-start seeding.
 *
 * Each loader returns the full MapDef (same wire shape as GET /api/v1/maps/:id).
 * The JSON files under assets/maps/ are copied from the engine output by
 * `pnpm --filter @warcarp/mobile bundle-maps` (scripts/bundle-maps.mjs).
 *
 * IMPORTANT rules:
 *   - Network is still authoritative. These bundled copies are ONLY used as
 *     placeholder data on cold launch (when the network has not yet
 *     responded). They are not written to the React Query cache; if the API
 *     returns a different value, the network copy wins for the session.
 *   - test-quad is intentionally excluded — it must never be bundled into the
 *     public build (the bundle-maps.mjs script enforces this at copy time).
 *   - This file must be updated whenever a new map is added to the engine.
 *     The bundle-maps.mjs script copies the JSON; this file wires the lazy
 *     require() calls so Metro includes them in the bundle.
 *
 * Metro requires() are statically analysed at build time, so `require()` calls
 * inside a function body are acceptable here — Metro picks them up.
 */

import type { MapDef } from '@warcarp/api-types'

/**
 * Map loaders are intentionally functions rather than eagerly-required values.
 * Metro still includes the JSON assets in the native bundle, but parsing and
 * retaining a map is deferred until that map is actually needed. This keeps a
 * cold launch from parsing every board just to render the home screen.
 */
const BUNDLED_MAP_LOADERS: Readonly<Record<string, () => MapDef>> = {
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'europe-classic': () => require('../assets/maps/europe-classic.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'antarctica': () => require('../assets/maps/antarctica.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'arctic': () => require('../assets/maps/arctic.json') as MapDef,
  'atlantis-rising': () => require('../assets/maps/atlantis-rising.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'carpathia': () => require('../assets/maps/carpathia.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'captureflag': () => require('../assets/maps/captureflag.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'castles': () => require('../assets/maps/castles.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'forbidden-city': () => require('../assets/maps/forbidden-city.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'crescent-isle': () => require('../assets/maps/crescent-isle.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'great-lakes': () => require('../assets/maps/great-lakes.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'island-of-the-dead': () => require('../assets/maps/island-of-the-dead.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'japan': () => require('../assets/maps/japan.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'middle-east': () => require('../assets/maps/middle-east.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'minnesota': () => require('../assets/maps/minnesota.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'normulli-sector': () => require('../assets/maps/normulli-sector.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'royal-rumble': () => require('../assets/maps/royal-rumble.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'russia': () => require('../assets/maps/russia.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'the-hex': () => require('../assets/maps/the-hex.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'usa': () => require('../assets/maps/usa.json') as MapDef,
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  'world-classic': () => require('../assets/maps/world-classic.json') as MapDef,
}

/** Load one bundled map on demand. Metro keeps these requires in the bundle. */
export function getBundledMap(id: string | undefined): MapDef | undefined {
  return id ? BUNDLED_MAP_LOADERS[id]?.() : undefined
}

/**
 * Derive the list-summary from bundled full MapDefs.
 *
 * This mirrors the shape of GET /api/v1/maps (production shape — no test maps,
 * since test-quad isn't bundled), so
 * useMaps() can seed the list query key on cold start from the same data
 * without a separate bundled index file.
 */
export interface BundledMapSummary {
  id: string
  name: string
  territoryCount: number
  regionCount: number
}

/** Selectable metadata for cold-start pickers; deprecated maps retain loaders only. */
export const BUNDLED_MAP_LIST: readonly BundledMapSummary[] = [
  { id: 'antarctica', name: 'Antarctica', territoryCount: 37, regionCount: 8 },
  { id: 'arctic', name: 'Arctic', territoryCount: 47, regionCount: 14 },
  { id: 'atlantis-rising', name: 'Atlantis Rising', territoryCount: 60, regionCount: 13 },
  { id: 'captureflag', name: 'Capture the Flag', territoryCount: 93, regionCount: 16 },
  { id: 'carpathia', name: 'Carpathia', territoryCount: 25, regionCount: 5 },
  { id: 'castles', name: 'Old World', territoryCount: 44, regionCount: 9 },
  { id: 'great-lakes', name: 'Great Lakes', territoryCount: 24, regionCount: 5 },
  { id: 'europe-classic', name: 'Europe Classic', territoryCount: 161, regionCount: 34 },
  { id: 'island-of-the-dead', name: 'Island of the Dead', territoryCount: 110, regionCount: 12 },
  { id: 'crescent-isle', name: 'Crescent Isle', territoryCount: 58, regionCount: 8 },
  { id: 'japan', name: 'Japan', territoryCount: 44, regionCount: 7 },
  { id: 'middle-east', name: 'Middle East', territoryCount: 58, regionCount: 16 },
  { id: 'minnesota', name: 'Minnesota', territoryCount: 87, regionCount: 12 },
  { id: 'normulli-sector', name: 'Normulli Sector', territoryCount: 252, regionCount: 47 },
  { id: 'royal-rumble', name: 'Royal Rumble', territoryCount: 73, regionCount: 28 },
  { id: 'russia', name: 'Russia', territoryCount: 52, regionCount: 10 },
  { id: 'the-hex', name: 'Hexcore', territoryCount: 43, regionCount: 13 },
  { id: 'usa', name: 'United States', territoryCount: 47, regionCount: 12 },
  { id: 'world-classic', name: 'World Classic', territoryCount: 42, regionCount: 6 },
]
